import React, { useEffect } from 'react';
import I18n from 'utils/I18n';
import PropTypes from 'prop-types';

import useApple from 'hooks/externalSocialPartners/useApple';
import { isEmpty } from 'lodash-es';
import clsx from '@strava/ui/clsx';
import styles from './index.scss';
import DisclaimerText from '../DisclaimerText';

import {
  FacebookButton,
  GoogleButton,
  EmailButton,
  SIGN_UP,
  FACEBOOK,
  GOOGLE,
  EMAIL,
  APPLE,
  elemAttrForAnalytics
} from '../AccountAuthButton';
import AppleButton from '../AccountAuthButton/AppleButton';

import {
  LANDING_PAGE,
  PRIVATE_ACTIVITY,
  MOBILE_REGISTRATION_PAGE,
  pageAttrForAnalytics,
  track
} from '../../../utils/onboarding';
import StravaAlert from '../../StravaAlert';

const AccountSignupForm = ({
  authorizationUrls,
  config,
  page,
  flashMessage,
  className
}) => {
  const { signin: appleSignin } = useApple(config.appleState);

  const I18nPrefix = 'strava.landing_page';

  useEffect(() => {
    if (page !== LANDING_PAGE) {
      track({
        page: pageAttrForAnalytics(page),
        action: 'screen_enter'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnClick = (clickedOption) => {
    if (clickedOption === EMAIL && page !== MOBILE_REGISTRATION_PAGE) {
      // Tracking for clicks on email signup on desktop homepage
      track({
        page: pageAttrForAnalytics(page),
        action: 'click',
        element: 'start_signup',
        properties: {
          signup_copy: 'email_signup'
        }
      });
    } else {
      // Tracking for clicks on social signup option on desktop homepage i.e. redirected to social auth flow
      // OR clicks on email signup on MOBILE_REGISTRATION_PAGE
      track({
        page: pageAttrForAnalytics(page),
        action: 'click',
        element: elemAttrForAnalytics(clickedOption)
      });
    }

    if (clickedOption === EMAIL && page !== MOBILE_REGISTRATION_PAGE) {
      // email button navigates to anchor #reg-step-2 on mobile
      window.location.href = authorizationUrls[EMAIL];
    } else if (clickedOption === APPLE) {
      appleSignin(config.appleState);
    } else {
      window.location.href = authorizationUrls[clickedOption];
    }
  };

  const orDivider = (
    <div className={styles.hr}>
      <div className={styles.hrText}>{I18n.t(`${I18nPrefix}.or`)}</div>
    </div>
  );

  return (
    <div className={clsx(className)}>
      {!isEmpty(flashMessage) && (
        <StravaAlert data-cy="alert" severity="error">
          {flashMessage}
        </StravaAlert>
      )}
      <FacebookButton type={SIGN_UP} onClick={() => handleOnClick(FACEBOOK)} />
      <GoogleButton type={SIGN_UP} onClick={() => handleOnClick(GOOGLE)} />
      {config.appleEnabled && (
        <AppleButton type={SIGN_UP} onClick={() => handleOnClick(APPLE)} />
      )}
      {page === LANDING_PAGE && orDivider}
      <EmailButton type={SIGN_UP} onClick={() => handleOnClick(EMAIL)} />
      <DisclaimerText showLoginOption={page === LANDING_PAGE} />
      {page === PRIVATE_ACTIVITY && orDivider}
    </div>
  );
};

AccountSignupForm.defaultProps = {
  flashMessage: null,
  className: null
};

AccountSignupForm.propTypes = {
  page: PropTypes.oneOf([
    LANDING_PAGE,
    PRIVATE_ACTIVITY,
    MOBILE_REGISTRATION_PAGE
  ]).isRequired,
  config: PropTypes.shape({
    appleEnabled: PropTypes.bool.isRequired,
    appleState: PropTypes.string.isRequired // https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
  }).isRequired,
  authorizationUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    facebook: PropTypes.string.isRequired
  }).isRequired,
  flashMessage: PropTypes.string,
  className: PropTypes.string
};

export default AccountSignupForm;
